import * as Sentry from '@sentry/react';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import type ft from 'firebase';
import { ulid } from 'ulid';
import {
  CollectionMemberStatus,
  FirestoreAssetCollection,
  FirestoreCollectionMember,
  getCollectionMemberDocId,
  parseCollectionMemberDocId,
} from '@creative-kit/shared';
import type { RootStore } from '.';
import { db } from './data';
import { db as firestoreDb, FieldValue } from 'firebase-modules';
import {
  buildCollectionStatusBreakdown,
  calendarIsoFromStoredDate,
} from './collection-status-sections';
import type { CollectionStatusBreakdown } from './collection-status-sections';
import { coerceFirestoreDate, isValidDate } from './firestore-date';

export interface AssetCollectionTreeNode {
  collection: FirestoreAssetCollection & { id: string };
  children: AssetCollectionTreeNode[];
}

export { getCollectionMemberDocId as getAssetKey, parseCollectionMemberDocId as parseAssetKey };

interface CollectionWithId extends FirestoreAssetCollection {
  id: string;
}

function buildMemberStatusFields(
  status: CollectionMemberStatus,
  options?: { scheduledDate?: Date; scheduleNotes?: string },
) {
  if (status === 'scheduled') {
    if (!options?.scheduledDate) {
      throw new Error('scheduledDate is required when status is scheduled');
    }
    const fields: Record<string, unknown> = {
      status,
      scheduledDate: options.scheduledDate,
    };
    // Firestore rejects `undefined` — omit to preserve existing notes, or delete when cleared.
    if (options.scheduleNotes !== undefined) {
      fields.scheduleNotes =
        options.scheduleNotes === '' ? FieldValue.delete() : options.scheduleNotes;
    }
    return fields;
  }
  return {
    status,
    scheduledDate: FieldValue.delete(),
    // scheduleNotes preserved for audit/history — matches transitionScheduledMembers cron
  };
}

function scheduledMemberExtras(options?: {
  scheduledDate?: Date;
  scheduleNotes?: string;
}): { scheduledDate: Date; scheduleNotes?: string } | Record<string, never> {
  if (!options?.scheduledDate) {
    return {};
  }
  return options.scheduleNotes
    ? { scheduledDate: options.scheduledDate, scheduleNotes: options.scheduleNotes }
    : { scheduledDate: options.scheduledDate };
}

export class AssetCollectionsStore {
  private rootStore: RootStore;
  private stopListeningToCollections: () => void = () => {};
  private disposeSessionReaction?: () => void;
  private memberUnsubscribers = new Map<string, () => void>();
  private currentSessionId?: string;

  collections: Record<string, CollectionWithId> = {};
  membersByCollectionId: Record<string, Record<string, FirestoreCollectionMember>> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      collections: observable,
      membersByCollectionId: observable,

      collectionsList: computed,
      visibleCollectionsTree: computed,
      hiddenCollections: computed,
      allMemberAssetKeys: computed,
      childCountByParentId: computed,
      membershipsByAssetDocId: computed,
      statusBreakdownByCollectionId: computed,

      createCollection: action.bound,
      updateCollection: action.bound,
      moveCollection: action.bound,
      deleteCollection: action.bound,
      duplicateCollection: action.bound,
      setCollectionHidden: action.bound,
      addMember: action.bound,
      removeMember: action.bound,
      updateMemberStatus: action.bound,
      addOrUpdateMemberStatus: action.bound,
      reset: action.bound,
    });
  }

  init() {
    this.disposeSessionReaction = reaction(
      () => this.rootStore.sessionsStore.currentSessionId,
      this.watchCollections,
      { fireImmediately: true },
    );
  }

  dispose() {
    this.disposeSessionReaction?.();
    this.disposeSessionReaction = undefined;
    this.reset();
  }

  reset() {
    this.stopListeningToCollections();
    this.memberUnsubscribers.forEach((unsub) => unsub());
    this.memberUnsubscribers.clear();
    this.collections = {};
    this.membersByCollectionId = {};
    this.currentSessionId = undefined;
    this.stopListeningToCollections = () => {};
  }

  watchCollections = () => {
    const sessionId = this.rootStore.sessionsStore.currentSessionId;
    if (!sessionId) {
      this.reset();
      return;
    }

    if (sessionId === this.currentSessionId) {
      return;
    }

    this.reset();
    this.currentSessionId = sessionId;

    this.stopListeningToCollections = db.assetCollections
      .where('sessionId', '==', sessionId)
      .onSnapshot(
        (snapshot: ft.firestore.QuerySnapshot<FirestoreAssetCollection>) => {
          runInAction(() => {
            snapshot
              .docChanges()
              .forEach((change: ft.firestore.DocumentChange<FirestoreAssetCollection>) => {
                const collectionId = change.doc.id;

                if (change.type === 'removed') {
                  delete this.collections[collectionId];
                  this.stopMemberListener(collectionId);
                  delete this.membersByCollectionId[collectionId];
                  return;
                }

                this.collections[collectionId] = {
                  id: collectionId,
                  ...change.doc.data(),
                };
                this.startMemberListener(collectionId);
              });
          });
        },
        (error: ft.firestore.FirestoreError) => {
          // eslint-disable-next-line no-console
          console.error(
            '[AssetCollectionsStore] Collections listener error:',
            error.code,
            error.message,
          );
        },
      );
  };

  private startMemberListener(collectionId: string) {
    if (this.memberUnsubscribers.has(collectionId)) {
      return;
    }

    const unsub = db.collectionMembers(collectionId).onSnapshot(
      (snapshot) => {
        runInAction(() => {
          const members: Record<string, FirestoreCollectionMember> = {};
          snapshot.docs.forEach((doc) => {
            members[doc.id] = doc.data();
          });
          this.membersByCollectionId[collectionId] = members;
        });
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error(
          `[AssetCollectionsStore] Members listener error (${collectionId}):`,
          error.code,
          error.message,
        );
      },
    );

    this.memberUnsubscribers.set(collectionId, unsub);
  }

  private stopMemberListener(collectionId: string) {
    const unsub = this.memberUnsubscribers.get(collectionId);
    if (unsub) {
      unsub();
      this.memberUnsubscribers.delete(collectionId);
    }
  }

  get collectionsList(): CollectionWithId[] {
    return Object.values(this.collections).sort((a, b) => a.position - b.position);
  }

  get visibleCollectionsTree(): AssetCollectionTreeNode[] {
    return buildCollectionTree(this.collectionsList.filter((c) => !c.hidden));
  }

  get hiddenCollections(): CollectionWithId[] {
    return this.collectionsList.filter((c) => c.hidden);
  }

  /** Union of all member doc IDs across collections — O(1) lookup for missing-asset filter. */
  get allMemberAssetKeys(): Set<string> {
    const keys = new Set<string>();
    Object.values(this.membersByCollectionId).forEach((members) => {
      Object.keys(members).forEach((memberDocId) => keys.add(memberDocId));
    });
    return keys;
  }

  get childCountByParentId(): Record<string, number> {
    const counts: Record<string, number> = {};
    this.collectionsList.forEach((collection) => {
      if (collection.parentId) {
        counts[collection.parentId] = (counts[collection.parentId] ?? 0) + 1;
      }
    });
    return counts;
  }

  /** Members of a parent collection that are not in any direct child subfolder. */
  getUnsortedMemberKeys(collectionId: string): Set<string> {
    const childIds = this.collectionsList
      .filter((c) => c.parentId === collectionId && !c.hidden)
      .map((c) => c.id);
    if (childIds.length === 0) {
      return new Set();
    }

    const inChild = new Set<string>();
    childIds.forEach((childId) => {
      Object.keys(this.membersByCollectionId[childId] ?? {}).forEach((memberKey) => {
        inChild.add(memberKey);
      });
    });

    const unsorted = new Set<string>();
    Object.keys(this.membersByCollectionId[collectionId] ?? {}).forEach((memberKey) => {
      if (!inChild.has(memberKey)) {
        unsorted.add(memberKey);
      }
    });
    return unsorted;
  }

  get membershipsByAssetDocId(): Record<
    string,
    Array<{ collection: CollectionWithId; member: FirestoreCollectionMember }>
  > {
    const byDocId: Record<
      string,
      Array<{ collection: CollectionWithId; member: FirestoreCollectionMember }>
    > = {};

    Object.entries(this.membersByCollectionId).forEach(([collectionId, members]) => {
      const collection = this.collections[collectionId];
      if (!collection) return;

      Object.entries(members).forEach(([memberDocId, member]) => {
        const list = byDocId[memberDocId] ?? [];
        list.push({ collection, member });
        byDocId[memberDocId] = list;
      });
    });

    Object.values(byDocId).forEach((list) =>
      list.sort((a, b) => a.collection.name.localeCompare(b.collection.name)),
    );

    return byDocId;
  }

  get statusBreakdownByCollectionId(): Record<string, CollectionStatusBreakdown> {
    const breakdowns: Record<string, CollectionStatusBreakdown> = {};
    Object.entries(this.membersByCollectionId).forEach(([collectionId, members]) => {
      breakdowns[collectionId] = buildCollectionStatusBreakdown(members);
    });
    return breakdowns;
  }

  getMemberAssetKeys(collectionId: string): Set<string> {
    const members = this.membersByCollectionId[collectionId] ?? {};
    return new Set(Object.keys(members));
  }

  isAssetInAnyCollection(sourceId: string, rowId: string): boolean {
    return this.allMemberAssetKeys.has(getCollectionMemberDocId(sourceId, rowId));
  }

  /** Calendar ISO dates (YYYY-MM-DD) for scheduled memberships of this asset. */
  getScheduledDatesForAsset(sourceId: string, rowId: string): string[] {
    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    const dates = new Set<string>();

    this.membershipsByAssetDocId[memberDocId]?.forEach(({ member }) => {
      if (member.status === 'scheduled' && member.scheduledDate) {
        const scheduled = coerceFirestoreDate(member.scheduledDate);
        if (isValidDate(scheduled)) {
          dates.add(calendarIsoFromStoredDate(scheduled));
        }
      }
    });

    return Array.from(dates);
  }

  getCollectionsForAsset(
    sourceId: string,
    rowId: string,
  ): Array<{ collection: CollectionWithId; member: FirestoreCollectionMember }> {
    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    return this.membershipsByAssetDocId[memberDocId] ?? [];
  }

  async createCollection(args: {
    name: string;
    emoji?: string;
    parentId?: string | null;
  }): Promise<string> {
    const sessionId = this.rootStore.sessionsStore.currentSessionId;
    const userId = this.rootStore.authStore.user?.uid;
    if (!sessionId || !userId) {
      throw new Error('Cannot create collection without an active session and user');
    }

    const siblings = this.collectionsList.filter((c) => c.parentId === (args.parentId ?? null));
    const now = new Date();
    const collectionId = ulid();
    const data: FirestoreAssetCollection = {
      sessionId,
      name: args.name.trim(),
      parentId: args.parentId ?? null,
      position: siblings.length,
      hidden: false,
      memberCount: 0,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
    };
    if (args.emoji) {
      data.emoji = args.emoji;
    }

    await db.assetCollections.doc(collectionId).set(data);
    return collectionId;
  }

  async updateCollection(
    collectionId: string,
    updates: Partial<
      Pick<FirestoreAssetCollection, 'name' | 'emoji' | 'parentId' | 'position' | 'hidden'>
    >,
  ): Promise<void> {
    if (updates.parentId === collectionId) {
      throw new Error('Collection cannot be its own parent');
    }

    if (
      updates.parentId !== undefined &&
      updates.parentId !== null &&
      this.wouldCreateParentCycle(collectionId, updates.parentId)
    ) {
      throw new Error('Cannot set parent: would create a circular collection hierarchy');
    }

    await db.assetCollections.doc(collectionId).update({
      ...Object.fromEntries(
        Object.entries(updates).filter((entry): entry is [string, NonNullable<typeof entry[1]>] => {
          return entry[1] !== undefined;
        }),
      ),
      updatedAt: new Date(),
    });
  }

  /** True when `nodeId` is the same as or nested under `ancestorId`. */
  isDescendantOf(ancestorId: string, nodeId: string): boolean {
    let current: string | null = nodeId;
    while (current) {
      if (current === ancestorId) {
        return true;
      }
      current = this.collections[current]?.parentId ?? null;
    }
    return false;
  }

  /**
   * Reparent and/or reorder a collection among its siblings.
   * Reindexes affected siblings in one batch write.
   */
  async moveCollection(
    collectionId: string,
    args: { parentId: string | null; insertIndex: number },
  ): Promise<void> {
    const collection = this.collections[collectionId];
    if (!collection) {
      throw new Error('Collection not found');
    }

    const oldParentId = collection.parentId;
    const newParentId = args.parentId;

    if (newParentId === collectionId) {
      throw new Error('Collection cannot be its own parent');
    }

    if (newParentId && this.wouldCreateParentCycle(collectionId, newParentId)) {
      throw new Error('Cannot move folder: would create a circular collection hierarchy');
    }

    const now = new Date();
    const batch = firestoreDb.batch();

    const getSortedSiblings = (parentId: string | null, excludeId?: string) =>
      this.collectionsList
        .filter((c) => c.parentId === parentId && c.id !== excludeId)
        .sort((a, b) => a.position - b.position);

    if (oldParentId !== newParentId) {
      getSortedSiblings(oldParentId, collectionId).forEach((sibling, index) => {
        if (sibling.position !== index) {
          batch.update(db.assetCollections.doc(sibling.id), { position: index, updatedAt: now });
        }
      });
    }

    const targetSiblings = getSortedSiblings(newParentId, collectionId);
    const clampedIndex = Math.max(0, Math.min(args.insertIndex, targetSiblings.length));
    targetSiblings.splice(clampedIndex, 0, collection);

    targetSiblings.forEach((sibling, index) => {
      const updates: {
        position: number;
        updatedAt: Date;
        parentId?: string | null;
      } = {
        position: index,
        updatedAt: now,
      };
      if (sibling.id === collectionId) {
        updates.parentId = newParentId;
      }
      batch.update(db.assetCollections.doc(sibling.id), updates);
    });

    await batch.commit();
  }

  /**
   * Best-effort client-side cycle check before reparenting.
   * Uses in-memory MobX state only — not a hard guarantee under concurrent edits.
   * MAX_TREE_DEPTH in CollectionTreeNode prevents render loops if data is corrupted.
   */
  // NOTE: Reads local MobX state only — concurrent editors can both pass this check.
  // MAX_TREE_DEPTH in CollectionTreeNode prevents render loops; a Cloud Function move is the long-term fix.
  private wouldCreateParentCycle(collectionId: string, newParentId: string): boolean {
    let current: string | null = newParentId;
    const visited = new Set<string>();

    while (current) {
      if (current === collectionId) {
        return true;
      }
      if (visited.has(current)) {
        return true;
      }
      visited.add(current);
      current = this.collections[current]?.parentId ?? null;
    }

    return false;
  }

  /**
   * Recursively deletes a collection, its descendants, and all member docs.
   * Not atomic — a mid-tree failure can leave orphaned docs with no rollback.
   * Acceptable for shallow client-side trees; long-term this belongs in a Cloud Function.
   */
  async deleteCollection(collectionId: string): Promise<void> {
    const collection = this.collections[collectionId];
    const sessionId = collection?.sessionId ?? this.rootStore.sessionsStore.currentSessionId;
    const authUid = this.rootStore.authStore.user?.uid;
    const debugContext = {
      collectionId,
      sessionId,
      authUid,
      collectionSessionId: collection?.sessionId,
      memberCount: collection?.memberCount,
    };

    const logDeleteFailure = (step: string, error: unknown) => {
      const firestoreError = error as ft.firestore.FirestoreError | undefined;
      // eslint-disable-next-line no-console
      console.error('[AssetCollectionsStore] deleteCollection failed', {
        step,
        ...debugContext,
        firestoreCode: firestoreError?.code,
        firestoreMessage: firestoreError?.message,
        error,
      });
    };

    try {
      // Use in-memory tree — a top-level query on parentId alone is rejected by Firestore
      // security rules in production (query must constrain sessionId when read rule uses it).
      const childIds = this.collectionsList
        .filter((c) => c.parentId === collectionId)
        .map((c) => c.id);
      await Promise.all(childIds.map((childId) => this.deleteCollection(childId)));

      let memberIds = Object.keys(this.membersByCollectionId[collectionId] ?? {});
      if (memberIds.length === 0 && (collection?.memberCount ?? 0) > 0) {
        // Listener may be stale; fall back to a scoped subcollection read (allowed by rules).
        const memberSnap = await db.collectionMembers(collectionId).get();
        memberIds = memberSnap.docs.map((doc) => doc.id);
      }

      // Firestore batch limit is 500 ops; 499 matches other batch chunking in the codebase.
      const BATCH_SIZE = 499;
      for (let i = 0; i < memberIds.length; i += BATCH_SIZE) {
        const batch = firestoreDb.batch();
        const chunk = memberIds.slice(i, i + BATCH_SIZE);
        chunk.forEach((memberId) => {
          batch.delete(db.collectionMembers(collectionId).doc(memberId));
        });
        try {
          await batch.commit();
        } catch (error) {
          logDeleteFailure(
            `deleteMembers(batch ${i / BATCH_SIZE + 1}, count ${chunk.length})`,
            error,
          );
          throw error;
        }
      }

      try {
        await db.assetCollections.doc(collectionId).delete();
      } catch (error) {
        logDeleteFailure('deleteCollectionDoc', error);
        throw error;
      }
    } catch (error) {
      logDeleteFailure('deleteCollection', error);
      Sentry.captureException(error, {
        tags: { operation: 'deleteCollection' },
        extra: debugContext,
      });
      const firestoreError = error as ft.firestore.FirestoreError | undefined;
      const codeSuffix = firestoreError?.code ? ` (${firestoreError.code})` : '';
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to delete collection ${collectionId}: ${message}${codeSuffix}`);
    }
  }

  /**
   * Duplicates a collection and its nested folders. Optionally copies member docs.
   */
  async duplicateCollection(
    collectionId: string,
    options: { includeMembers: boolean; newParentId?: string | null; depth?: number },
  ): Promise<string> {
    const depth = options.depth ?? 0;
    const MAX_DUPLICATE_DEPTH = 20;
    if (depth > MAX_DUPLICATE_DEPTH) {
      throw new Error('Collection tree is too deeply nested to duplicate');
    }

    const source = this.collections[collectionId];
    if (!source) {
      throw new Error('Collection not found');
    }

    const newId = await this.createCollection({
      name: `${source.name} (copy)`,
      emoji: source.emoji,
      parentId: options.newParentId ?? source.parentId,
    });

    if (options.includeMembers) {
      // Always read from Firestore — MobX member cache may lag the sidebar count (BDD + fast duplicate).
      const memberSnap = await db.collectionMembers(collectionId).get();
      const members = memberSnap.docs.map((doc) => doc.data());
      await Promise.all(
        members.map((member) =>
          this.addOrUpdateMemberStatus(
            newId,
            member.sourceId,
            member.rowId,
            member.status,
            member.status === 'scheduled' && member.scheduledDate
              ? {
                  scheduledDate: coerceFirestoreDate(member.scheduledDate),
                  ...(member.scheduleNotes ? { scheduleNotes: member.scheduleNotes } : {}),
                }
              : undefined,
          ),
        ),
      );
    }

    const children = this.collectionsList.filter((c) => c.parentId === collectionId);
    await Promise.all(
      children.map((child) =>
        this.duplicateCollection(child.id, {
          includeMembers: options.includeMembers,
          newParentId: newId,
          depth: depth + 1,
        }),
      ),
    );

    return newId;
  }

  async setCollectionHidden(collectionId: string, hidden: boolean): Promise<void> {
    await this.updateCollection(collectionId, { hidden });
  }

  /** Direct child subfolders of a parent collection (non-hidden). */
  private getDirectChildCollectionIds(collectionId: string): string[] {
    return this.collectionsList
      .filter((c) => c.parentId === collectionId && !c.hidden)
      .map((c) => c.id);
  }

  /**
   * When assigning an asset to a parent collection, remove it from direct child
   * subfolders first so it appears under Unsorted rather than in both places.
   */
  private async evictMemberFromDirectChildFolders(
    parentCollectionId: string,
    sourceId: string,
    rowId: string,
  ): Promise<void> {
    const childIds = this.getDirectChildCollectionIds(parentCollectionId);
    if (childIds.length === 0) {
      return;
    }

    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    for (const childId of childIds) {
      if (this.membersByCollectionId[childId]?.[memberDocId]) {
        await this.removeMember(childId, sourceId, rowId);
      }
    }
  }

  async addMember(
    collectionId: string,
    sourceId: string,
    rowId: string,
    status: CollectionMemberStatus = 'pending',
    options?: { scheduledDate?: Date; scheduleNotes?: string },
  ): Promise<void> {
    const userId = this.rootStore.authStore.user?.uid;
    if (!userId) {
      throw new Error('Cannot add collection member without an authenticated user');
    }

    if (status === 'scheduled' && !options?.scheduledDate) {
      throw new Error('scheduledDate is required when status is scheduled');
    }

    await this.evictMemberFromDirectChildFolders(collectionId, sourceId, rowId);

    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    const member: FirestoreCollectionMember = {
      sourceId,
      rowId,
      status,
      addedAt: new Date(),
      addedBy: userId,
      ...(status === 'scheduled' ? scheduledMemberExtras(options) : {}),
    };

    const memberRef = db.collectionMembers(collectionId).doc(memberDocId);
    const collectionRef = db.assetCollections.doc(collectionId);

    await firestoreDb.runTransaction(async (transaction) => {
      const existingMember = await transaction.get(memberRef);
      if (existingMember.exists) {
        return;
      }

      transaction.set(memberRef, member);
      transaction.update(collectionRef, {
        memberCount: FieldValue.increment(1) as unknown as number,
        updatedAt: new Date(),
      });
    });
  }

  async removeMember(collectionId: string, sourceId: string, rowId: string): Promise<void> {
    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    const memberRef = db.collectionMembers(collectionId).doc(memberDocId);
    const collectionRef = db.assetCollections.doc(collectionId);

    await firestoreDb.runTransaction(async (transaction) => {
      const existingMember = await transaction.get(memberRef);
      if (!existingMember.exists) {
        return;
      }

      transaction.delete(memberRef);
      transaction.update(collectionRef, {
        memberCount: FieldValue.increment(-1) as unknown as number,
        updatedAt: new Date(),
      });
    });
  }

  async updateMemberStatus(
    collectionId: string,
    sourceId: string,
    rowId: string,
    status: CollectionMemberStatus,
    options?: { scheduledDate?: Date; scheduleNotes?: string },
  ): Promise<void> {
    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    const memberRef = db.collectionMembers(collectionId).doc(memberDocId);

    await firestoreDb.runTransaction(async (transaction) => {
      const existingMember = await transaction.get(memberRef);
      if (!existingMember.exists) {
        Sentry.addBreadcrumb({
          category: 'asset-collections',
          level: Sentry.Severity.Warning,
          message: 'updateMemberStatus: member not found',
          data: { collectionId, memberDocId },
        });
        return;
      }

      transaction.update(memberRef, buildMemberStatusFields(status, options));
    });
  }

  async addOrUpdateMemberStatus(
    collectionId: string,
    sourceId: string,
    rowId: string,
    status: CollectionMemberStatus,
    options?: { scheduledDate?: Date; scheduleNotes?: string },
  ): Promise<void> {
    if (status === 'scheduled' && !options?.scheduledDate) {
      throw new Error('scheduledDate is required when status is scheduled');
    }

    const userId = this.rootStore.authStore.user?.uid;
    if (!userId) {
      throw new Error('Cannot update collection member without an authenticated user');
    }

    await this.evictMemberFromDirectChildFolders(collectionId, sourceId, rowId);

    const memberDocId = getCollectionMemberDocId(sourceId, rowId);
    const memberRef = db.collectionMembers(collectionId).doc(memberDocId);
    const collectionRef = db.assetCollections.doc(collectionId);

    // Always upsert in Firestore — do not branch on MobX local state (stale under concurrency).
    await firestoreDb.runTransaction(async (transaction) => {
      const existingMember = await transaction.get(memberRef);

      if (!existingMember.exists) {
        const member: FirestoreCollectionMember = {
          sourceId,
          rowId,
          status,
          addedAt: new Date(),
          addedBy: userId,
          ...(status === 'scheduled' ? scheduledMemberExtras(options) : {}),
        };
        transaction.set(memberRef, member);
        transaction.update(collectionRef, {
          memberCount: FieldValue.increment(1) as unknown as number,
          updatedAt: new Date(),
        });
        return;
      }

      transaction.update(memberRef, buildMemberStatusFields(status, options));
    });
  }
}

function buildCollectionTree(collections: CollectionWithId[]): AssetCollectionTreeNode[] {
  const byParent = new Map<string | null, CollectionWithId[]>();

  collections.forEach((collection) => {
    const parentKey = collection.parentId;
    const siblings = byParent.get(parentKey) ?? [];
    siblings.push(collection);
    byParent.set(parentKey, siblings);
  });

  const buildNodes = (parentId: string | null): AssetCollectionTreeNode[] => {
    const siblings = (byParent.get(parentId) ?? []).sort((a, b) => a.position - b.position);
    return siblings.map((collection) => ({
      collection,
      children: buildNodes(collection.id),
    }));
  };

  return buildNodes(null);
}
